/**
 * Page: HubSupportPage
 * HUB-12-12 — Support inbox / inquiry queue. Inbound emails to
 * support@<INBOUND_EMAIL_DOMAIN> land here via the existing Resend
 * webhook (internal/api/email_bridge.go). Replies go out via Resend
 * with Reply-To set to support+inq-<id>@<INBOUND_EMAIL_DOMAIN> so
 * customer follow-ups thread back into the same inquiry.
 *
 * HUB-REDESIGN P5 — reshell to bento pattern: 4-KPI strip header
 * (Open / Closed wk / Backlog / CSAT) + functional inbox card below.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Card, KPI, DepartmentObservability } from './components'
import { hubSupport, type SupportInquiry, type SupportReply } from './api'

const P = {
  surface:   'var(--bg-surface)',
  elevated:  'var(--bg-elevated)',
  border:    'var(--border)',
  text:      'var(--text-primary)',
  textMuted: 'var(--text-secondary)',
  textDim:   'var(--text-muted)',
  danger:    'var(--st-error)',
  active:    'var(--st-active)',
  warning:   'var(--st-warning)',
}

type StatusFilter = 'open' | 'closed' | 'all'

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'open',   label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'all',    label: 'All' },
]

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day}d ago`
  const mo = Math.floor(day / 30)
  if (mo < 12) return `${mo}mo ago`
  return `${Math.floor(mo / 12)}y ago`
}

export default function HubSupportPage() {
  const [status, setStatus] = useState<StatusFilter>('open')
  const [items, setItems] = useState<SupportInquiry[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [selected, setSelected] = useState<SupportInquiry | null>(null)
  const [detail, setDetail] = useState<{ inquiry: SupportInquiry; replies: SupportReply[] } | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [replyBody, setReplyBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [actionErr, setActionErr] = useState<string | null>(null)

  const refresh = async (preserveSelectedId?: number) => {
    setLoading(true)
    setErr(null)
    try {
      const r = await hubSupport.list(status)
      setItems(r.inquiries)
      if (preserveSelectedId != null) {
        const match = r.inquiries.find((i) => i.id === preserveSelectedId) ?? null
        setSelected(match)
      } else if (selected) {
        const stillThere = r.inquiries.find((i) => i.id === selected.id)
        if (!stillThere) setSelected(null)
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  useEffect(() => {
    let cancelled = false
    if (!selected) {
      setDetail(null)
      return
    }
    setDetailLoading(true)
    hubSupport
      .get(selected.id)
      .then((d) => { if (!cancelled) setDetail(d) })
      .catch((e) => { if (!cancelled) setActionErr(e instanceof Error ? e.message : 'Detail load failed') })
      .finally(() => { if (!cancelled) setDetailLoading(false) })
    return () => { cancelled = true }
  }, [selected])

  // KPIs reflect current filter; cross-status totals would need a separate fetch.
  const kpis = useMemo(() => {
    const now = Date.now()
    const open = items.filter((i) => i.status === 'open')
    const closed = items.filter((i) => i.status === 'closed')

    const openCount = open.length
    const openAged = open.filter((i) => {
      const ts = i.received_at ? new Date(i.received_at).getTime() : 0
      return ts > 0 && now - ts > SEVEN_DAYS_MS
    }).length

    const closedWk = closed.filter((i) => {
      const closedTs = i.closed_at
        ? new Date(i.closed_at).getTime()
        : i.last_activity_at ? new Date(i.last_activity_at).getTime() : 0
      return closedTs > 0 && now - closedTs <= SEVEN_DAYS_MS
    }).length

    const backlog = open.filter((i) => {
      const ts = i.last_activity_at ? new Date(i.last_activity_at).getTime() : 0
      return ts > 0 && now - ts > SEVEN_DAYS_MS
    }).length

    return { openCount, openAged, closedWk, backlog }
  }, [items])

  const onSendReply = async () => {
    if (!selected) return
    const body = replyBody.trim()
    if (!body) {
      setActionErr('Reply body is empty')
      return
    }
    setSubmitting(true)
    setActionErr(null)
    try {
      await hubSupport.reply(selected.id, body)
      setReplyBody('')
      const refreshed = await hubSupport.get(selected.id)
      setDetail(refreshed)
      void refresh(selected.id)
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : 'Send failed')
    } finally {
      setSubmitting(false)
    }
  }

  const onClose = async () => {
    if (!selected) return
    setSubmitting(true)
    setActionErr(null)
    try {
      await hubSupport.close(selected.id)
      void refresh(selected.id)
      const refreshed = await hubSupport.get(selected.id)
      setDetail(refreshed)
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : 'Close failed')
    } finally {
      setSubmitting(false)
    }
  }

  const onReopen = async () => {
    if (!selected) return
    setSubmitting(true)
    setActionErr(null)
    try {
      await hubSupport.reopen(selected.id)
      void refresh(selected.id)
      const refreshed = await hubSupport.get(selected.id)
      setDetail(refreshed)
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : 'Reopen failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ padding: 14, color: P.text, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
      {/* KPI strip — bento header */}
      <Card style={{ padding: 0 }} bodyPad={false}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1, background: 'var(--border)' }}>
          <div style={{ background: 'var(--surface)' }}>
            <KPI
              label="Open"
              value={kpis.openCount}
              delta={kpis.openCount === 0 ? 'all clear' : `${kpis.openAged} aged >7d`}
              tone={kpis.openCount === 0 ? 'up' : 'down'}
              endpoint="/hub/support/inquiries"
            />
          </div>
          <div style={{ background: 'var(--surface)' }}>
            <KPI
              label="Closed wk"
              value={kpis.closedWk}
              delta="—"
              sub="first-reply avg not yet computed"
              tone="flat"
            />
          </div>
          <div style={{ background: 'var(--surface)' }}>
            <KPI
              label="Backlog"
              value={kpis.backlog}
              delta={kpis.backlog > 0 ? 'aged >7d' : 'none aged >7d'}
              tone={kpis.backlog > 0 ? 'down' : 'flat'}
            />
          </div>
          <div style={{ background: 'var(--surface)' }}>
            <KPI
              label="CSAT"
              value="—"
              delta="not collected"
              tone="flat"
              endpoint="BACKEND_NEEDED: csat"
            />
          </div>
        </div>
      </Card>

      {/* Functional inbox — wrapped in design-system Card */}
      <Card
        title="Support inbox"
        endpoint="/hub/support/inquiries"
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {STATUS_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => setStatus(o.value)}
                className={`btn${status === o.value ? '' : ' ghost'}`}
                style={{ fontSize: 11, padding: '4px 10px' }}
              >
                {o.label}
              </button>
            ))}
            <button
              onClick={() => refresh()}
              disabled={loading}
              className="btn ghost"
              style={{ fontSize: 11, padding: '4px 10px', opacity: loading ? 0.5 : 1 }}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        }
        bodyPad={false}
      >
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
          {err && (
            <div style={{ padding: 12, background: P.surface, border: `1px solid ${P.danger}`, borderRadius: 8, color: P.danger, fontSize: 13 }}>
              {err}
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: '380px 1fr',
            gap: 12,
            flex: 1,
            minHeight: 0,
            // ensure inbox has reasonable height inside a Card body
            height: 'calc(100vh - 280px)',
          }}>
            {/* List column */}
            <div style={{
              background: P.surface,
              border: `1px solid ${P.border}`,
              borderRadius: 8,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}>
              <div style={{
                padding: '10px 14px',
                borderBottom: `1px solid ${P.border}`,
                fontSize: 12,
                color: P.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}>
                {items.length} inquir{items.length === 1 ? 'y' : 'ies'}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {!loading && items.length === 0 && (
                  <div style={{ padding: 24, color: P.textDim, fontSize: 13, textAlign: 'center' }}>
                    No {status === 'all' ? '' : status} inquiries.
                  </div>
                )}
                {items.map((it) => (
                  <button
                    key={it.id}
                    onClick={() => setSelected(it)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 14px',
                      border: 'none',
                      borderBottom: `1px solid ${P.border}`,
                      background: selected?.id === it.id ? P.elevated : 'transparent',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      color: P.text,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {it.from_name || it.from_email}
                      </span>
                      <span style={{ fontSize: 11, color: P.textDim, flexShrink: 0 }}>{fmtRelative(it.last_activity_at)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: P.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                      {it.subject || '(no subject)'}
                    </div>
                    <div style={{ fontSize: 11, color: P.textDim, display: 'flex', gap: 8 }}>
                      <span>{it.from_email}</span>
                      {it.reply_count > 0 && <span>· {it.reply_count} repl{it.reply_count === 1 ? 'y' : 'ies'}</span>}
                      {it.status === 'closed' && <span style={{ color: P.textDim }}>· closed</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Detail column */}
            <div style={{
              background: P.surface,
              border: `1px solid ${P.border}`,
              borderRadius: 8,
              padding: 16,
              overflowY: 'auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}>
              {!selected && (
                <div style={{ color: P.textDim, fontSize: 13, textAlign: 'center', padding: 40 }}>
                  Select an inquiry to view + reply.
                </div>
              )}

              {selected && detailLoading && (
                <div style={{ color: P.textDim, fontSize: 13 }}>Loading…</div>
              )}

              {selected && detail && (
                <>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, marginBottom: 4 }}>
                          {detail.inquiry.subject || '(no subject)'}
                        </h2>
                        <div style={{ fontSize: 13, color: P.textMuted }}>
                          {detail.inquiry.from_name && detail.inquiry.from_name !== detail.inquiry.from_email
                            ? `${detail.inquiry.from_name} <${detail.inquiry.from_email}>`
                            : detail.inquiry.from_email}
                        </div>
                        <div style={{ fontSize: 12, color: P.textDim, marginTop: 2 }}>
                          Received {fmtRelative(detail.inquiry.received_at)} · to {detail.inquiry.to_address}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        {detail.inquiry.status === 'open' ? (
                          <button
                            onClick={onClose}
                            disabled={submitting}
                            className="btn ghost"
                            style={{ fontSize: 12, opacity: submitting ? 0.5 : 1 }}
                          >
                            Close
                          </button>
                        ) : (
                          <button
                            onClick={onReopen}
                            disabled={submitting}
                            className="btn ghost"
                            style={{ fontSize: 12, opacity: submitting ? 0.5 : 1 }}
                          >
                            Reopen
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Original inbound body */}
                  <div style={{
                    background: P.elevated,
                    border: `1px solid ${P.border}`,
                    borderRadius: 8,
                    padding: 12,
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: P.text,
                  }}>
                    {detail.inquiry.body_text || '(empty body)'}
                  </div>

                  {/* Outbound replies */}
                  {detail.replies.map((r) => (
                    <div key={r.id} style={{
                      background: P.surface,
                      border: `1px solid ${P.border}`,
                      borderRadius: 8,
                      padding: 12,
                      marginLeft: 24,
                    }}>
                      <div style={{ fontSize: 12, color: P.textMuted, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                        <span>{r.admin_name || r.admin_email} (you)</span>
                        <span style={{ color: P.textDim }}>{fmtRelative(r.sent_at)}</span>
                      </div>
                      <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: P.text }}>
                        {r.body}
                      </div>
                    </div>
                  ))}

                  {/* Reply box */}
                  {detail.inquiry.status === 'open' && (
                    <div style={{
                      marginTop: 8,
                      border: `1px solid ${P.border}`,
                      borderRadius: 8,
                      padding: 12,
                      background: P.surface,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}>
                      <div style={{ fontSize: 12, color: P.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Reply
                      </div>
                      <textarea
                        value={replyBody}
                        onChange={(e) => setReplyBody(e.target.value)}
                        placeholder="Write a reply…"
                        rows={6}
                        style={{
                          width: '100%',
                          padding: 10,
                          borderRadius: 6,
                          border: `1px solid ${P.border}`,
                          background: P.elevated,
                          color: P.text,
                          fontFamily: 'inherit',
                          fontSize: 13,
                          resize: 'vertical',
                          boxSizing: 'border-box',
                        }}
                      />
                      {actionErr && (
                        <div style={{ color: P.danger, fontSize: 12 }}>{actionErr}</div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: P.textDim }}>
                          Customer follow-ups thread back automatically.
                        </span>
                        <button
                          onClick={onSendReply}
                          disabled={submitting || !replyBody.trim()}
                          className="btn primary"
                          style={{ fontSize: 13, opacity: submitting || !replyBody.trim() ? 0.5 : 1 }}
                        >
                          {submitting ? 'Sending…' : 'Send reply'}
                        </button>
                      </div>
                    </div>
                  )}

                  {detail.inquiry.status === 'closed' && (
                    <div style={{ color: P.textDim, fontSize: 12, textAlign: 'center', padding: 12 }}>
                      This inquiry is closed. Reopen it to send a reply.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      <DepartmentObservability department="support" />
    </div>
  )
}
